// dependencies
import * as d3 from "d3";
import { format } from "date-fns";

const generateGraph = (svgContainer, data, onSelect) => {
	// clear the graph ( if one exists )
	d3.select(svgContainer).select("svg").remove();

	const margin = { top: 10, right: 50, bottom: 30, left: 60 };
	const height = 200 - margin.top - margin.bottom;
	const width = svgContainer.offsetWidth - margin.left - margin.right;

	const svg = d3
		.select(svgContainer)
		.append("div")
		.append("svg")
		.attr("class", "timeseries-container")
		.attr("width", width + margin.left + margin.right)
		.attr("height", height + margin.top + margin.bottom);

	const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

	// x-axis should be the same for all data sets
	const x = d3
		.scaleTime()
		.rangeRound([0, width])
		.domain(d3.extent(data[0].data, (d) => d.date));

	const brushed = (event) => {
		if (!event.sourceEvent) return; // Only transition after input.

		if (!event.selection) {
			onSelect(null);
			return;
		}

		// get selection time boundary
		const selection = event.selection.map(x.invert);

		// round the boundaries to the nearest minute
		const roundedSelection = selection.map(d3.timeMinute.round);

		// move the selection to the nearest minute
		d3.select(this).transition().call(event.target.move, roundedSelection.map(x));

		// inform the hook
		onSelect(roundedSelection);
	};

	let requestsY = null;
	let maxResponseTimeY = null;
	let uniqueUsersY = null;
	let meanResponseTimeY = null;

	// generate a y axis for each data set
	for (const object of data) {
		const dataObject = object;

		const max = Math.ceil(d3.max(dataObject.data, (d) => d.value) / dataObject.maxRound) * dataObject.maxRound;

		const y = d3.scaleLinear().domain([0, max]).rangeRound([height, 0]);

		if (object.id === "requestsPerMinute") {
			requestsY = y;
		} else if (object.id === "uniqueUsersCount") {
			uniqueUsersY = y;
		} else if (object.id === "maxResponseTime") {
			maxResponseTimeY = y;
		} else if (object.id === "meanResponseTime") {
			meanResponseTimeY = y;
		}

		const line = d3
			.line()
			.x((d) => x(d.date))
			.y((d) => y(d.value));

		dataObject.svg = {
			y,
			max,
			line,
		};
	}

	const yAxisRequests = d3
		.axisLeft(requestsY)
		.tickValues([0, data[0].svg.max * 0.5, data[0].svg.max])
		.tickSize(-width, 0, 0);

	const yAxisMean = d3
		.axisRight(maxResponseTimeY)
		.tickValues([0, data[1].svg.max * 0.5, data[1].svg.max])
		.tickSize(width, 0, 0);

	const yAxisMeanResponse = d3
		.axisRight(meanResponseTimeY)
		.tickValues([0, data[2].svg.max * 0.5, data[2].svg.max])
		.tickSize(width, 0, 0);

	const yAxisUsers = d3
		.axisLeft(uniqueUsersY)
		.tickValues([0, data[3].svg.max * 0.5, data[3].svg.max])
		.tickSize(-width, 0, 0);

	const xAxis = d3.axisBottom(x).tickFormat((d) => format(d, "h:mm a"));

	const brush = d3
		.brushX()
		.on("end", brushed)
		.extent([
			[0, 0],
			[width, height],
		]);

	g.append("g").attr("class", "x axis").attr("transform", `translate(0,${height})`).call(xAxis);

	g.append("g")
		.attr("class", "y axis left requests-axis")
		.call(yAxisRequests)
		.style("transform", "translate(-30px, 0)")
		.append("text")
		.attr("transform", "rotate(-90)")
		.attr("y", 6)
		.attr("dy", ".71em");

	g.append("g")
		.attr("class", "y axis left users-axis")
		.style("transform", "translate(-10px, 0)")
		.call(yAxisUsers)
		.append("text")
		.attr("transform", "rotate(-90)")
		.attr("y", 6)
		.attr("dy", ".71em");

	g.append("g")
		.attr("class", "y axis right mean-axis")
		.call(yAxisMean)
		.append("text")
		.attr("transform", "rotate(-90)")
		.attr("y", 6)
		.attr("dy", ".71em");

	g.append("g")
		.attr("class", "y axis right mean-response-axis")
		.call(yAxisMeanResponse)
		.style("transform", "translate(-24px, 0)")
		.append("text")
		.attr("transform", "rotate(-90)")
		.attr("y", 6)
		.attr("dy", ".71em");

	const item = g.selectAll(".item").data(data).enter().append("g").attr("class", "item");

	item.append("path")
		.attr("class", (d) => `line ${d.id}`)
		.attr("d", (d) => d.svg.line(d.data))
		.style("stroke-dasharray", (d) => d.strokeDash);

	g.append("g").attr("class", "x brush").call(brush);
};

export default generateGraph;
